import {stringify} from '@api'
import {observable, action, computed, runInAction} from 'mobx'
import {site, nav, onSiteError, Stores, ui} from '@stores'
import {SYNC_MERCH_OPTION_ES, SYNC_MERCH_PRODUCT_ES, SYNC_MERCH_COLLECTION_ES, CANCEL_MERCH_COLLECTION_SYNC, MERCH_SYNC_PROGRESS_STATUS, SYNC_BUSINESS_OBJECT} from './'
import {SyncTriggers} from '@modules/syncIndicator/HeaderSyncIndicator'
import { StoresInstance } from '@stores/StoresInstance'
import axios, { AxiosInstance } from 'axios'
import {configs} from '@stores/config'
import { authStore } from '@utils'
import { deployments } from '@stores/dev'

export interface ISyncProgressStatus {
  percent?: number,
  itemId?: string,
  show?: boolean,
  cancellable?: boolean,
  loading?: boolean
}

interface IMerchSyncProgressStatus {
  data: {
    merchSyncProgressStatus?: any,
    input?: any
  }
}

export type SyncParam = {
  itemId: string,
  syncId: string,
  versionsToSync?: Array<string>,
  trigger?: SyncTriggers
}

export class SyncProgressStore {
  constructor(public stores: Partial<Stores>) {}


  @observable byId = observable.map<string, ISyncProgressStatus>()

  @computed get inprogressIds() {
    let count = 0
    this.byId.forEach((syncStatus: ISyncProgressStatus, id) => {
      if (syncStatus?.percent < 100) {
        count++
      }
    })
    return count
  }

  //During GraphQL implementation this will be changed to be used in common for GANT and ASICS VLP

  // private readonly closeOnFailure = {
  //     'vlp-merch-product-version': stores.sidebar.onGoBack,
  //     'vlp-merch-option-version': stores.product.onProductClose
  // }

  /**
   * TODO - change mutation in server to take the id instead of having multiple mutations clientside
   */
  mutationFor = {
    merchProductSyncStatus: SYNC_MERCH_PRODUCT_ES,
    merchOptionSyncStatus: SYNC_MERCH_OPTION_ES,
    merchCollectionSyncStatus: SYNC_MERCH_COLLECTION_ES
  }

  cancelMutationFor = {
    merchCollectionSyncStatus: CANCEL_MERCH_COLLECTION_SYNC,
    workspaceSyncStatus: CANCEL_MERCH_COLLECTION_SYNC
  }
  private status: any

  @observable syncItemId: string

  //@observable status: number = 0

  @action
  getMerchSyncProgressStatus() {
    const {stores: {apollo: {client}}, syncItemId} = this

    const pollingCycle = syncItemId && client.watchQuery<{ input }, {}>({
      variables: {
        syncItemId
      },
      query: MERCH_SYNC_PROGRESS_STATUS,
      fetchPolicy: 'network-only'
    } as any)
    return pollingCycle
  }

  @observable cycleCount = 0

  @action stopPollingCycle = () => {
    const cycle = this.getMerchSyncProgressStatus()
    if (cycle) {
      cycle.stopPolling()
      this.stores.flexLayout.refetchCatalogTabs()
    }
  }

  @action setData = () => {
    const {stores: {flexLayout: {activeCatalogRoot}, sidebar: {right: {details: {data}}}}} = this,
      previousData = activeCatalogRoot ? activeCatalogRoot.stores.catalog.itemDetailStores.get(this.syncItemId) : null

    if (data && previousData) {
      previousData._data = data
    }
  }

  @action syncPollingCycle = (id: string) => {
    const {byId, stores: {ui, vlp: {hierarchy: {fetchLastSyncTime}}}} = this,
      syncStatus = byId.get(id)

    if (syncStatus?.itemId) {
      const {itemId} = syncStatus
      this.syncItemId = itemId
      !syncStatus?.loading && runInAction(() => {
        syncStatus.show = true
        syncStatus.percent = 0
        syncStatus.loading = true
        byId.set(id, syncStatus)
      })
      const cycle = this.getMerchSyncProgressStatus()
      if (cycle) {
        runInAction(() => {
          // store.status = 0
        })
        cycle.startPolling(ui.settings.sync.pollInterval)
        cycle.subscribe((res: any) => {
          const {data: {merchSyncProgressStatus: {syncCompletion, isSyncCancellable}}} = res
          runInAction(() => {
            if (parseInt(syncCompletion) > syncStatus.percent) {
              syncStatus.percent = parseInt(syncCompletion)
              syncStatus.cancellable = isSyncCancellable
              // store.status = status.percent
              syncStatus.loading = true
              byId.set(id, syncStatus)
            }
          })

          if (syncCompletion == 100) {
            cycle.stopPolling()
            runInAction(() => {
              byId.set(id, syncStatus)
              fetchLastSyncTime('internal')
              this.checkAndRefreshDetailsData()
              setTimeout(() => this.reset(id), 3000)
              syncStatus.show = false
              syncStatus.percent = 100
              syncStatus.loading = false
            })
          }
        }, (error) => {

          this.cycleCount++
          cycle.stopPolling()
          if (this.cycleCount < 15) {
            this.syncPollingCycle(id)
          } else {
            this.reset(id)
          }
        })
      }
    }
  }

  @action onStartSync = async ({itemId, syncId, versionsToSync, trigger}: SyncParam, fireSyncMutation: boolean = true) => {
    const {byId, stores: {apollo, catalog, catalog: {index, filter: {selectedHierarchyGroupFilters}}}} = this

    if (!byId.has(itemId)) {
      byId.set(itemId, {loading: true, cancellable: true})
    }
    let itemStatus = byId.get(itemId)
    itemStatus.itemId = itemId
    itemStatus.cancellable = true
    itemStatus.loading = true
    itemStatus.percent = 0

    // const interval = setInterval(()=>{
    //   if(itemStatus.percent < 100){
    //     itemStatus.percent += Math.floor(Math.random() * 11)
    //     if(itemStatus.percent>=100){
    //       itemStatus.percent = 100
    //       runInAction(() => {
    //         itemStatus.show = false
    //         itemStatus.percent = 100
    //         itemStatus.loading = false
    //         this.reset(itemId)
    //         byId.set(itemId, itemStatus)
    //         // byId.delete(id)
    //       })
    //     }
    //     byId.set(itemId, itemStatus)
    //   }
    // },2000)
    // return
    const selectedHierarchyGroupFiltersObj = Object.fromEntries(selectedHierarchyGroupFilters)
    if (fireSyncMutation) {
      try {
        const variables: any = {where: {exIds: [itemId], esIndex: syncId, payload: {versionsToSync, index, trigger, selectedHierarchyGroupFiltersObj}}}

        const {data: {syncBusinessObject: {error, success}}} = await apollo.client.mutate({
          mutation: SYNC_BUSINESS_OBJECT,
          variables
        })
        await this.stores.deployments.invalidateC8DynamicConfig()
        if (!_.isEmpty(error)) {
          let message = error?.message
          const {workspaces: {labelForIndex}} = this.stores
          if (error?.extra) {
            // extra will be the array of indices which are in progress for sync at present
            message = 'Sync already in progress for ' + error.extra.map((index) => labelForIndex(index)).join(', ').trim() + ' . Multiple workspace sync is not allowed'
          }
          onSiteError(message)
          this.stopPollingCycle()
          this.reset(itemId)
          return
        }
        this.syncPollingCycle(itemId)
        catalog.refetch()

      } catch (e) {
        if (e?.message) {
          onSiteError(e?.message)
        }
        console.warn(e, 'sync timeout but sync is still in Progress')
      } finally {
        await catalog?.debounceFetchWorkspaceCounts()
        if (this.status === 100) {
          this.reset(itemId)
        }
      }
    } else {
      this.syncPollingCycle(itemId)
    }
  }

  @observable versions: Array<string> = []

  isMerchCollectionSyncing = async (itemId: string, versionsToSync?) => {
    // console.trace(`isMerchCollectionSyncing`, {itemId, versionsToSync})
    const {byId, stores: {workspaces, apollo: {client}}} = this
    if (!byId.has(itemId)) {
      byId.set(itemId, {loading: true})
    }
    const status = this.byId.get(itemId)
    status.loading = true
    let data: IMerchSyncProgressStatus = await client.query<{ input }, {}>({
      query: MERCH_SYNC_PROGRESS_STATUS,
      variables: {
        syncItemId: itemId,
        versionsToSync
      },
      fetchPolicy: 'network-only'
    } as any)
    status.loading = false
    this.stopPollingCycle()
    const progressStatus = data?.data?.merchSyncProgressStatus
    if (progressStatus && progressStatus.syncCompletion < 100 && progressStatus.isSyncCancellable) {
      this.onStartSync({itemId, syncId: workspaces.workspaceConfig?.syncIndicator?.syncId, versionsToSync}, false)
      const syncSeasons = progressStatus?.versions?.split(',')

      this.versions = syncSeasons
      return true
    }
    return false
  }

  cancelSync = async (itemId: string, syncId: string) => {
    const {stores: {strings, apollo}} = this
    const mutation = CANCEL_MERCH_COLLECTION_SYNC//cancelMutationFor[syncId]

    if (!mutation) {
      console.error(`No query to cancel sync ${syncId}`)
      throw new Error(strings.syncNotRunning)
    }
    try {
      // StopPollingCycle()
      const success = await apollo.client.query<{ input }, {}>({
        variables: {
          syncItemId: itemId
        },
        query: mutation,
        fetchPolicy: 'network-only'
      } as any)
      console.log({success})
    } finally {
      this.reset(itemId)
    }
  }

  checkAndRefreshDetailsData = ()=>{
    StoresInstance?.allStores?.forEach(stores => {
      stores?.catalog?.sidebar?.right?.details && stores.catalog.sidebar.right.details.loadData()
    })
    //(this.stores.flexLayout.tabsByComponent?.inlineCatalogItemDetails ?? []).forEach(t => t?.storesToInject && t.storesToInject.catalog.sidebar.right.details.loadData())
  }

  reset = (syncId) => {
    const {stores: {sidebar, catalog, flexLayout}} = this
    if (syncId == 'merchProductSyncStatus' || syncId == 'merchOptionSyncStatus') catalog.refetch()
    this.byId.delete(syncId)
    this.stopPollingCycle()
    if (syncId !== 'workspaceSyncStatus' && syncId !== 'merchCollectionSyncStatus') {
      const detailsStore = sidebar?.right?.details
      if(detailsStore?.data?.id === syncId) {
        detailsStore.onRefreshImages()
      }
    }
  }

  @computed get byIdMap() {
    console.log('BY ID MAP CHANGING')
    return this.byId
  }
}



// WEBPACK FOOTER //
// ./src/stores/sync/SyncProgressStore.ts